import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { z } from "zod";
import { SiteShell } from "@/components/site-shell";
import { useAuth } from "@/hooks/use-auth";
import { resolveNextPath } from "@/lib/auth-redirect";
import { GoogleAuthButton } from "@/components/google-auth-button";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export const Route = createFileRoute("/signup")({
  head: () => ({
    meta: [{ title: "Sign up — DealDraft" }],
    links: [{ rel: "canonical", href: "https://dealdraft.pro/signup" }],
  }),
  validateSearch: z.object({ next: z.string().optional() }).catch({}),
  component: SignupPage,
});

function SignupPage() {
  const { supabase } = useAuth();
  const navigate = useNavigate();
  const { next } = Route.useSearch();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!supabase) return;
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: window.location.origin,
      },
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    if (!data.session) {
      toast.success("Account created! Check your email to confirm.");
      navigate({ to: "/login", search: next ? { next } : {} });
    } else {
      toast.success("Welcome to DealDraft");
      const { to, search } = resolveNextPath(next);
      navigate({ to: to as "/" | "/generate" | "/dashboard", search: search as never });
    }
  }

  return (
    <SiteShell>
      <section className="mx-auto flex max-w-md flex-col px-6 py-16">
        <h1 className="text-3xl font-semibold tracking-tight">Create your free account</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          3 free generations every month. No credit card.
        </p>

        <div className="mt-8 space-y-4 rounded-xl border border-border bg-card p-6 shadow-sm">
          <GoogleAuthButton label="Sign up with Google" next={next} />
          <div className="relative py-1 text-center text-xs text-muted-foreground">
            <span className="bg-card px-2">or continue with email</span>
            <div className="absolute inset-x-0 top-1/2 -z-10 border-t border-border" />
          </div>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="full_name">Full name</Label>
              <Input
                id="full_name"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="confirm">Confirm password</Label>
              <Input
                id="confirm"
                type="password"
                required
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <Button type="submit" className="w-full" disabled={submitting || !supabase} data-testid="signup-submit">
              {submitting ? "Creating account…" : "Create free account"}
            </Button>
          </form>
        </div>
        <p className="mt-6 text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-primary hover:underline">
            Log in
          </Link>
        </p>
      </section>
    </SiteShell>
  );
}
